import { Box } from '@mui/material';
import React, { forwardRef, ReactNode } from 'react';

interface PrintableSectionProps {
  children: ReactNode;
  /** Unique identifier for this printable section */
  id: string;
  /** Title to display when printing */
  title?: string;
  /** Additional content to show only when printing (e.g., metadata, timestamps) */
  printHeader?: ReactNode;
  /** Additional content to show only when printing (e.g., footer, disclaimers) */
  printFooter?: ReactNode;
  /** CSS class name for custom styling */
  className?: string;
}

/**
 * A wrapper component that marks content as printable.
 * Wrap any content you want to be exportable to PDF with this component.
 * 
 * @example
 * ```tsx
 * <PrintableSection id="compliance-table" title="Compliance Report">
 *   <ComplianceDetailTableComponent />
 * </PrintableSection>
 * ```
 */
export const PrintableSection = forwardRef<HTMLDivElement, PrintableSectionProps>(
  ({ children, id, title, printHeader, printFooter, className }, ref) => {
    return (
      <Box
        ref={ref}
        id={id}
        className={`printable-section ${className || ''}`}
        data-print-title={title}
        sx={{
          position: 'relative',
          '@media print': {
            pageBreakInside: 'avoid',
          },
        }}
      >
        {/* Print-only header */}
        {(title || printHeader) && (
          <Box
            className="print-only"
            sx={{
              display: 'none',
              '@media print': {
                display: 'block',
                marginBottom: 2,
                paddingBottom: 1,
                borderBottom: '2px solid #333',
              },
            }}
          >
            {title && (
              <Box
                component="h1"
                sx={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#000',
                }}
              >
                {title}
              </Box>
            )}
            {printHeader}
          </Box>
        )}

        {/* Main content */}
        <Box className="printable-content">{children}</Box>

        {/* Print-only footer */}
        {printFooter && (
          <Box
            className="print-only"
            sx={{
              display: 'none',
              '@media print': {
                display: 'block',
                marginTop: 2,
                paddingTop: 1,
                borderTop: '1px solid #ccc',
                fontSize: '12px',
                color: '#666',
              },
            }}
          >
            {printFooter}
          </Box>
        )}
      </Box>
    );
  }
);

PrintableSection.displayName = 'PrintableSection';

export default PrintableSection;

